/* eslint-disable jsx-a11y/aria-role */
import {
  type ISbStoriesParams,
  ISbStory,
  type ISbStoryData,
  StoryblokComponent,
  useStoryblokState,
} from '@storyblok/react';
import * as Sentry from '@sentry/nextjs';
import { getStoryblokApi } from '@storyblok/react';
import { GetServerSideProps } from 'next';
import { SSRConfig } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRouter } from 'next/router';
import NextNprogress from 'nextjs-progressbar';
import Custom404 from 'pages/404';
import { serializeSalaryData } from '@_pages/salary-insight/serializer';
import {
  type SelectSalaryDataQuery,
  type SelectSalaryDataQueryVariables,
  SelectSalaryData,
} from '@_pages/salary-insight/data';
import { initializeApollo } from '@apollo-client';
import { SalaryInsightInfo, type SalaryInsightEligbility } from '@_pages/salary-insight/types';
import {
  levelAbbrToSlugMapping,
  roleAbbreviationToSlugMapping,
  roleNameToSlugMapping,
  salaryDataVersion,
  SalaryInsightSlugs,
} from '@_pages/salary-insight/constants';
import { getKeyByValue } from '@helpers/general.utils';
import { isMappingValue } from '@helpers/type.utils';
import { slugifyToCapitalize } from '@helpers/titleize';
import { fetchSalaryInsightEligibility } from '@_pages/salary-insight/services';

type SalaryInsightPageProps = {
  country: string;
  role: string | null;
  level: string | null;
  salaryInsightEligbility: SalaryInsightEligbility;
  salaryInsightInfo: SalaryInsightInfo;
  story: ISbStoryData<void>;
  key: string | false;
  preview: boolean;
  footer: ISbStory;
} & SSRConfig;

async function fetchSalaryData(
  country: string,
  role: string | null,
  level: string | null,
  version: number,
) {
  const apolloClient = initializeApollo();

  let and_where: SelectSalaryDataQueryVariables['and_where'] = [];

  if (!role) {
    and_where.push({ role: { _is_null: true } });
  } else {
    const roleKey = getKeyByValue(
      roleAbbreviationToSlugMapping,
      role as (typeof roleAbbreviationToSlugMapping)[keyof typeof roleAbbreviationToSlugMapping],
    );
    and_where.push({ role: { _eq: roleKey } });
  }

  if (!level) {
    and_where.push({ level: { _is_null: true } });
  } else {
    // @ts-expect-error we get string from the url and it cannot be part of a defined literal
    const levelKey = getKeyByValue(levelAbbrToSlugMapping, level);
    and_where.push({ level: { _eq: levelKey } });
  }

  const { data, error } = await apolloClient.query<
    SelectSalaryDataQuery,
    SelectSalaryDataQueryVariables
  >({
    query: SelectSalaryData,
    context: { clientName: 'hasura' },
    fetchPolicy: 'cache-first',
    variables: {
      version,
      country: slugifyToCapitalize(country),
      and_where,
    },
  });

  if (error) {
    throw new Error(`Error fetching Salary Data: ${error}`);
  }

  return serializeSalaryData(data);
}

async function fetchFooter(
  storyblokApi: ReturnType<typeof getStoryblokApi>,
  preview: boolean,
  cv: number,
) {
  return await storyblokApi.getStory(`private-global-components/footers/footer`, {
    version: preview ? 'draft' : 'published',
    cv,
    // https://www.storyblok.com/faq/link-object-history#resolve_links-url
    resolve_links: 'url',
  });
}

export const getServerSideProps: GetServerSideProps<SalaryInsightPageProps> = async ({
  params,
  locale,
  preview,
  resolvedUrl,
}) => {
  const { slug } = params || {};

  if (!slug) {
    return {
      notFound: true, // This will render the 404 page
    };
  }

  let [country, role, level] = Array.isArray(slug) ? slug : [slug];

  if (!country) {
    return {
      notFound: true, // This will render the 404 page
    };
  }

  if (role && !isMappingValue(role, roleNameToSlugMapping)) {
    if (role !== SalaryInsightSlugs.allRoles) {
      Sentry.captureException(new Error('Invalid role '), {
        extra: {
          context: 'Invalid salary insight role',
          slug,
        },
      });
    }

    role = null;
  }

  if (level && !isMappingValue(level, levelAbbrToSlugMapping)) {
    Sentry.captureException(new Error('Invalid level '), {
      extra: {
        context: 'Invalid salary insight level',
        slug,
      },
    });

    level = null;
  }

  try {
    const storyblokApi = getStoryblokApi();

    const {
      data: { story, cv },
    } = await storyblokApi.get(`cdn/stories/${SalaryInsightSlugs.root}/${country}`, {
      version: preview ? 'draft' : 'published',
      resolve_relations: ['globalComponent.story'],
      language: locale,
    } satisfies ISbStoriesParams);

    const [salaryInsightEligbility, salaryInsightInfo, footer] = await Promise.all([
      fetchSalaryInsightEligibility(salaryDataVersion),
      fetchSalaryData(country, role, level, salaryDataVersion),
      fetchFooter(storyblokApi, preview, cv),
    ]);

    return {
      props: {
        ...(await serverSideTranslations(locale)),
        story: story || false,
        key: story?.id || false,
        preview: !!preview,
        country,
        role: role || null,
        level: level || null,
        salaryInsightEligbility,
        salaryInsightInfo,
        footer,
      },
    };
  } catch (error: unknown) {
    // When storyblok cannot find the slug it throws
    // Error: {"message":"Not Found","status":404,"response":"This record could not be found"}
    if (error['status'] === 404) {
      return {
        notFound: true,
      };
    }

    Sentry.captureException(error, {
      extra: {
        context: 'Failed at fetching data for storyblok component',
        resolvedUrl,
        status: error['status'] || 'unknown',
      },
    });

    throw error;
  }
};

export default function SalaryInsightPage({
  story,
  preview,
  country,
  role,
  level,
  footer,
  salaryInsightEligbility,
  salaryInsightInfo,
  _nextI18Next,
}: SalaryInsightPageProps) {
  const router = useRouter();
  story = useStoryblokState(story, {
    language: _nextI18Next.initialLocale,
  });

  if (router.isFallback)
    return (
      <NextNprogress
        color="#2D445D"
        startPosition={0.3}
        stopDelayMs={200}
        height={3}
        showOnShallow={true}
      />
    );

  if (!story) return <Custom404 />;

  return (
    <StoryblokComponent
      blok={story.content}
      page={{
        slug: story.slug,
        full_slug: story.full_slug,
        realPath: story.path, // storyblok real path if it exists
        name: story.name,
        locale: _nextI18Next.initialLocale,
        extra: {
          country,
          role,
          level,
          salaryInsightEligbility,
          salaryInsightInfo,
          footer,
        },
        preview,
      }}
    />
  );
}
